import { cn } from '@lib/util/cn';
import type { OptionValueAvailability } from '@modules/products/utils/variant-options';
import type React from 'react';
import OptionSelectDropdown from './option-select-dropdown';

const OPTION_ROW_THRESHOLD = 6;
const OPTION_DROPDOWN_THRESHOLD = 10;
const OPTION_ROW_SIZE = 5;

type ProductOptionShape = {
  id: string;
  values?: Array<{ value: string }> | null;
};

type OptionSelectProps = {
  option: ProductOptionShape;
  current: string | undefined;
  updateOption: (title: string, value: string) => void;
  title: string;
  disabled: boolean;
  // Per-value buyability/stock, keyed by option value. When omitted, every
  // value renders as before (nothing marked). See `getOptionValueAvailabilities`.
  valueStates?: Record<string, OptionValueAvailability>;
  'data-testid'?: string;
};

// Classifies a value from its availability: 'unbuyable' is a permanent dead end
// (missing metadata / not-for-sale) so the option is disabled; 'out-of-stock'
// stays selectable so the shopper can still reach the back-in-stock CTA.
type OptionValueMark = 'unbuyable' | 'out-of-stock' | null;

const markForValue = (
  state: OptionValueAvailability | undefined,
): OptionValueMark => {
  if (!state) return null;
  if (!state.buyable) return 'unbuyable';
  if (!state.inStock) return 'out-of-stock';
  return null;
};

const sortOptionValues = (values: string[]): string[] =>
  [...values].sort((a, b) => {
    const tokenize = (s: string) =>
      s.match(/(\d+(\.\d+)?)|([^\d]+)/g)?.map((t) => {
        const n = parseFloat(t);
        return Number.isNaN(n) ? t.toLowerCase() : n;
      }) ?? [];

    const ta = tokenize(a);
    const tb = tokenize(b);
    for (let i = 0; i < Math.min(ta.length, tb.length); i++) {
      const x = ta[i];
      const y = tb[i];
      if (typeof x === 'number' && typeof y === 'number' && x !== y)
        return x - y;
      if (typeof x === 'string' && typeof y === 'string') {
        const c = x.localeCompare(y, undefined, { sensitivity: 'base' });
        if (c !== 0) return c;
      }
    }
    return ta.length - tb.length;
  });

const OptionSelect: React.FC<OptionSelectProps> = ({
  option,
  current,
  updateOption,
  title,
  'data-testid': dataTestId,
  disabled,
  valueStates,
}) => {
  const sortedValues = sortOptionValues(
    (option.values ?? []).map((v) => v.value),
  );

  if (sortedValues.length > OPTION_DROPDOWN_THRESHOLD) {
    return (
      <OptionSelectDropdown
        optionId={option.id}
        title={title}
        values={sortedValues}
        current={current}
        updateOption={updateOption}
        disabled={disabled}
        valueStates={valueStates}
        data-testid={dataTestId}
      />
    );
  }

  const isCapped = sortedValues.length > OPTION_ROW_THRESHOLD;

  return (
    <fieldset className="flex flex-col gap-y-1 border-0 m-0 p-0">
      <legend className="text-sm text-brand-primary-800 mb-1" translate="no">
        {title === 'Variant' ? 'Select Option' : title}
      </legend>
      <div
        className="flex flex-wrap gap-2 items-stretch"
        data-testid={dataTestId}
      >
        {sortedValues.map((v) => {
          const mark = markForValue(valueStates?.[v]);
          const isUnbuyable = mark === 'unbuyable';
          const isOutOfStock = mark === 'out-of-stock';
          const isDeadOrOos = isUnbuyable || isOutOfStock;
          let markLabel: string | undefined;
          if (isUnbuyable) {
            markLabel = 'Unavailable';
          } else if (isOutOfStock) {
            markLabel = 'Out of stock';
          }
          return (
            <button
              type="button"
              onClick={(e) => {
                updateOption(option.id, v);
                e.currentTarget.blur();
              }}
              key={v}
              style={{
                flexBasis: isCapped
                  ? `calc(${100 / OPTION_ROW_SIZE}% - ${(0.5 * (OPTION_ROW_SIZE - 1)) / OPTION_ROW_SIZE}rem)`
                  : 'auto',
                flexGrow: 1,
                flexShrink: 0,
              }}
              className={cn(
                'border-ui-border-base cursor-pointer bg-ui-bg-subtle border nw-typography-paragraph rounded-rounded px-3 py-2 min-w-[80px] whitespace-nowrap transition-all duration-150 text-center disabled:cursor-not-allowed',
                {
                  'border-fg-interactive-strong bg-surface-accent-subtle text-fg-interactive-soft':
                    v === current && !isDeadOrOos,
                  'hover:shadow-elevation-card-rest hover:border-gray-300':
                    v !== current && !isDeadOrOos,
                },
                // Dead end (unbuyable) or temporarily out-of-stock — struck
                // through on a muted grey tile per design. Still selectable
                // when out-of-stock (back-in-stock CTA); the "Out Of Stock"
                // badge next to the price carries the label in words. Border
                // darkens from neutral/200 to neutral/600 when this value is
                // the current selection, instead of the usual teal highlight.
                {
                  'line-through text-ui-fg-muted bg-brand-neutral-100 border-brand-neutral-200':
                    isDeadOrOos,
                  'border-brand-neutral-600': isDeadOrOos && v === current,
                },
              )}
              disabled={disabled || isUnbuyable}
              aria-label={
                markLabel ? `${v} — ${markLabel.toLowerCase()}` : undefined
              }
              data-testid="option-button"
              translate="no"
            >
              {v}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
};

export default OptionSelect;
